import React from 'react';
import PropTypes from 'prop-types';
import { ArrowLabel, Spacing, Text } from '@reservamos/elements';
import arrowLine from 'images/arrow-line.svg';
import AnimationOverview from './AnimationOverview';

/**
 * Represents a trip schedule component.
 *
 * @param {Object} props - departureHour, arrivalHour, mainContent, mobileSize, departureTerminal, arrivalTerminal, showTerminals, hideMainContent.
 * @param {string} props.departureHour - The departure hour of the trip.
 * @param {string} props.arrivalHour - The arrival hour of the trip.
 * @param {string|array} props.mainContent - The main content of the trip.
 * @param {string} props.mobileSize - The mobile size of the component.
 * @param {string} props.departureTerminal - The departure terminal name.
 * @param {string} props.arrivalTerminal - The arrival terminal name.
 * @param {boolean} props.showTerminals - Whether to show terminal names.
 * @param {boolean} props.hideMainContent - When true, hides the main content and shows only the arrow line.
 * @returns {JSX.Element} The rendered trip schedule component.
 */
const TripSchedule = ({
  departureHour,
  arrivalHour,
  mainContent,
  mobileSize,
  departureTerminal,
  arrivalTerminal,
  showTerminals,
  hideMainContent,
}) => {
  const contentElement =
    typeof mainContent === 'object' ? (
      <div className="animation-content">
        <AnimationOverview contentTypes={mainContent} />
      </div>
    ) : (
      <Text color="grayLight" size="XS" textAlign="center">
        {mainContent}
      </Text>
    );
  const mainElement = hideMainContent ? null : contentElement;
  const departureText = (
    <Text mobileSize={mobileSize} size="XL" weight="semibold">
      {departureHour}
      {showTerminals && departureTerminal && (
        <Text size="XS" weight="regular">
          {departureTerminal}
        </Text>
      )}
    </Text>
  );

  const arrivalText = (
    <Text mobileSize={mobileSize} size="XL">
      {arrivalHour}
      {showTerminals && arrivalTerminal && (
        <Text size="XS" weight="regular">
          {arrivalTerminal}
        </Text>
      )}
    </Text>
  );
  if (!mainElement) {
    return (
      <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
        {departureText}
        <img src={arrowLine} alt="" />
        {arrivalText}
      </Spacing>
    );
  }

  return (
    <>
      {departureText}
      <ArrowLabel>{mainElement}</ArrowLabel>
      {arrivalText}
    </>
  );
};

TripSchedule.propTypes = {
  departureHour: PropTypes.string,
  arrivalHour: PropTypes.string,
  mainContent: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  mobileSize: PropTypes.string,
  departureTerminal: PropTypes.string,
  arrivalTerminal: PropTypes.string,
  showTerminals: PropTypes.bool,
  hideMainContent: PropTypes.bool,
};

TripSchedule.defaultProps = {
  showTerminals: false,
  mobileSize: 'M',
  departureTerminal: '',
  arrivalTerminal: '',
  hideMainContent: false,
};

export default TripSchedule;
